<script setup lang="ts">
import { hexAlpha } from '@/utils/levels';

// Layout desktop: página única de scroll. No topo, cartão do jogador e pódio de
// elos lado a lado (o pódio na coluna de 400px à direita, como no protótipo),
// com pitch e fatos do elo logo abaixo dos dois; depois o carrossel dos 14
// elos, o comparativo e as dúvidas. A barra de simulação de XP do protótipo não
// vem — era ferramenta de protótipo.
const {
  xp,
  level,
  isMaxLevel,
  tier,
  highlightTierIndex,
  levelProgress,
  xpToNextLevel,
  currentLevelLabel,
  nextLevelLabel,
  playerName,
  playerAvatar,
  openChangeNickname,
  viewSteps,
  viewIndex,
  viewTierIndex,
  viewTier,
  viewState,
  goToStep,
  goToTier,
  stepTier,
  viewXpRange,
  levelXpTable,
} = useLevelsProgress();

/** O fundo acompanha o elo em foco no pódio. */
const pageBackground = computed(
  () =>
    `radial-gradient(76% 50% at 50% 0%,${hexAlpha(viewTier.value.color, 0.22)} 0%,#020716 46%,#020716 76%)`,
);

// O ID vem da carteira, não da Smartico: é o mesmo número que o dropdown de
// perfil mostra, e o protótipo põe ele na plaquinha ao lado do nome.
const { currentUserID } = storeToRefs(useWalletStore());

const playerId = computed(() =>
  currentUserID.value ? `#${currentUserID.value}` : '',
);

/**
 * Fagulhas subindo atrás do pódio. Posições derivadas do índice (não de
 * `Math.random`) para o fundo não "pular" a cada re-render.
 */
const particles = computed(() =>
  Array.from({ length: 34 }, (_, i) => ({
    left: `${(i * 29 + 7) % 100}%`,
    size: `${2 + ((i * 5) % 3)}px`,
    duration: `${11 + ((i * 7) % 9)}s`,
    delay: `-${(i * 13) % 13}s`,
    color: i % 3 === 0 ? viewTier.value.accent : viewTier.value.color,
  })),
);
</script>

<template>
  <div
    class="relative w-full overflow-x-hidden text-white"
    :style="{ background: pageBackground }"
  >
    <!-- Fagulhas -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute left-0 right-0 top-0 z-[1] h-[620px] overflow-hidden"
      style="
        mask-image: linear-gradient(
          180deg,
          #000 0%,
          #000 58%,
          transparent 100%
        );
        -webkit-mask-image: linear-gradient(
          180deg,
          #000 0%,
          #000 58%,
          transparent 100%
        );
      "
    >
      <span
        v-for="(spark, index) in particles"
        :key="index"
        class="levels-drift absolute -bottom-3 rounded-full"
        :style="{
          left: spark.left,
          width: spark.size,
          height: spark.size,
          background: spark.color,
          boxShadow: `0 0 7px ${spark.color}`,
          animationDuration: spark.duration,
          animationDelay: spark.delay,
        }"
      ></span>
    </div>

    <div
      class="relative z-[2] mx-auto flex w-full max-w-[1180px] flex-col gap-14 px-8 pb-20 pt-8"
    >
      <!--
        Cartão de XP à esquerda e a fila de elos na coluna de 400px à direita,
        como no protótipo desktop. Abaixo de `xl` volta a empilhar: com 1024px
        de tela a coluna fixa comeria quase metade da largura e o cartão ficava
        estrangulado.
      -->
      <div class="flex flex-col gap-6">
        <LevelsBetaNotice />

        <div class="grid items-center gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
          <LevelsDesktopPlayerCard
            :avatar="playerAvatar"
            :is-max-level="isMaxLevel"
            :level="level"
            :level-label="currentLevelLabel"
            :next-level-label="nextLevelLabel"
            :player-id="playerId"
            :player-name="playerName"
            :progress="levelProgress"
            :tier="tier"
            :xp-to-next="xpToNextLevel"
            @edit-name="openChangeNickname"
          />

          <LevelsTierPodium
            :player-level-label="currentLevelLabel"
            :state="viewState"
            :steps="viewSteps"
            :view-index="viewIndex"
            :view-tier="viewTier"
            :xp-range="viewXpRange"
            @select="goToStep"
            @step="stepTier"
          />
        </div>
      </div>

      <!-- O carrossel fala em índice de ELO (0 = Bronze): recebe o índice
           traduzido (-1 quando o foco é o degrau Sem elo) e devolve pelo
           goToTier, que soma o degrau de volta.
           `-mt-16` come o gap-14 da coluna e mais um pouco: a base do palco
           dos pilares é esfumaçado vazio (máscara), então sem o ajuste fica
           um buraco enorme até os 14 elos. -->
      <LevelsTierCarousel
        class="-mt-16"
        :level-xp-table="levelXpTable"
        :player-tier-index="highlightTierIndex"
        :view-index="viewTierIndex"
        :xp="xp"
        @select="goToTier"
      />

      <LevelsBenefitMatrix :player-tier-index="highlightTierIndex" />

      <LevelsFaqAccordion :tier="tier" />
    </div>
  </div>
</template>

<style scoped lang="postcss">
@keyframes levelsDrift {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  18% {
    opacity: 0.55;
  }
  100% {
    transform: translateY(-620px) translateX(18px);
    opacity: 0;
  }
}

.levels-drift {
  animation-name: levelsDrift;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
</style>
